import {
  BasicOutlet,
  Button,
  DocumentInput,
  Flex,
  Heading,
  IconName,
  Message,
  Paragraph,
  toast
} from '@payflip/ui/v2';
import { handleFileDownload } from '@payflip/utils';
import { useAuth } from '@payflip/web/auth';
import { useContext, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router';
import { LocaleContext } from '../../../components/locale-provider';
import { environment } from '../../../environment';
import { GetBudgetsDocument } from '../../../screens/home/components/budgets/queries.generated';
import { GetEmployeeBudgetsDocument } from '../../../screens/your-budgets/queries.generated';
import { useActivateEmployeeBudgetMutation } from '../mutations.generated';
import { useGetBudgetQuery } from '../queries.generated';
import { useGetManualSigningOutletQuery } from './queries.cms.generated';

export function ManualSigningOutlet(): JSX.Element {
  const { getAccessToken } = useAuth();
  const { goBackTo } = useOutletContext<{ goBackTo: string }>();
  const { budgetId, templateId } = useParams<{
    budgetId: string;
    templateId: string;
  }>();
  const { locale } = useContext(LocaleContext);
  const [downloading, setDownloading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const navigate = useNavigate();
  const goBack = (): void | Promise<void> => navigate(goBackTo);

  const { data: budgetData } = useGetBudgetQuery({
    variables: { budgetId: budgetId ?? '' }
  });

  const { data: cmsData } = useGetManualSigningOutletQuery({
    locale
  });

  const commonCms = cmsData?.common?.data?.attributes ?? null;
  const outletCms =
    cmsData?.employeeBudgetActivationOutlet?.data?.attributes ?? null;
  const toastCms = cmsData?.adminToast?.data?.attributes ?? null;

  const [activateEmployeeBudget] = useActivateEmployeeBudgetMutation({
    onCompleted: (data) => {
      toast.add({
        type: 'success',
        body: (
          <Message
            text={toastCms?.genericSuccessMessage}
            fallback={'The budget is successfully activated.'}
          />
        ),
        action: {
          label: (
            <Message
              text={outletCms?.budgetSuccessfullyActivatedToastText}
              fallback={'View budget'}
            />
          ),
          click: () => {
            navigate(`/your-space/budgets/${data?.activateEmployeeBudget}`);
          }
        },
        icon: IconName.Success
      });
      setConfirming(false);
      goBack();
    },
    onError: () => {
      toast.add({
        type: 'warning',
        body: (
          <Message
            text={toastCms?.genericErrorMessage}
            fallback={'Someting went wrong. Please try again.'}
          />
        ),
        icon: IconName.Warning
      });
      setConfirming(false);
    },
    refetchQueries: [GetEmployeeBudgetsDocument, GetBudgetsDocument]
  });

  const budgetName =
    budgetData?.budget?.name[locale] ?? budgetData?.budget?.name?.en ?? '';
  const budgetTemplates = budgetData?.budget?.activationTemplates ?? [];

  const budgetTemplate = budgetTemplates.find((bt) => bt?.id === templateId);

  const handleFundingDocumentDownload = async (): Promise<void> => {
    setDownloading(true);
    const token = (await getAccessToken()) ?? '';
    const downloadAnchor = await handleFileDownload(
      `${environment.REST_API_URI}/budgets/funding-attachment/${budgetId}/${templateId}`,
      token,
      `${budgetName.toLowerCase().replace(/\s/g, '_')}.pdf`
    );

    document.body.appendChild(downloadAnchor);

    downloadAnchor.click();
    downloadAnchor.remove();

    setDownloading(false);
  };

  const handleSignedDocumentUpload = async (): Promise<void> => {
    setConfirming(true);
    const formData = new FormData();

    if (uploadedFile) {
      formData.append('file', uploadedFile);
    }

    const token = (await getAccessToken()) ?? '';
    const res = await fetch(`${environment.REST_API_URI}/blobs`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      body: formData
    }).then((res) => res.json());

    if (res.id) {
      await activateEmployeeBudget({
        variables: {
          budgetId: budgetId ?? '',
          blobId: res.id,
          templateId: templateId ?? ''
        }
      });
    }
  };

  if (!budgetTemplate) {
    return (
      <BasicOutlet>
        <BasicOutlet.Scrollable>
          <BasicOutlet.Header backgroundColor={'palette_blue.5'}>
            <BasicOutlet.BackButton
              onBack={goBack}
              text={outletCms?.goBackLink ?? 'Go back'}
            />
            <BasicOutlet.Heading variant={'h3'}>
              <Message
                text={outletCms?.confirmSignatureTitle}
                fallback={'Confirm signature'}
              />
            </BasicOutlet.Heading>
          </BasicOutlet.Header>
          <BasicOutlet.Main gap={7}>
            <Paragraph>
              <Message
                text={outletCms?.noTemplateFoundText}
                fallback={'No template found.'}
              />
            </Paragraph>
          </BasicOutlet.Main>
        </BasicOutlet.Scrollable>
        <BasicOutlet.Footer>
          <Button flexGrow={1} variant="secondary" onClick={goBack}>
            <Message text={commonCms?.cancel} fallback={'Cancel'} />
          </Button>
          <Button flexGrow={3} type="submit" isDisabled>
            <Message
              text={outletCms?.confirmSignatureButtonText}
              fallback={'Confirm signature'}
            />
          </Button>
        </BasicOutlet.Footer>
      </BasicOutlet>
    );
  }

  return (
    <BasicOutlet>
      <BasicOutlet.Scrollable>
        <BasicOutlet.Header backgroundColor={'palette_blue.5'}>
          <BasicOutlet.BackButton onBack={goBack} />
          <BasicOutlet.Heading variant={'h3'}>
            <Message
              text={outletCms?.confirmSignatureTitle}
              fallback={'Confirm signature'}
              variables={{
                budget: budgetName
              }}
            />
          </BasicOutlet.Heading>
        </BasicOutlet.Header>
        <BasicOutlet.Main gap={7}>
          <Flex direction={'column'} gap={3}>
            <Heading variant={'h4'}>
              <Message
                text={outletCms?.downloadFundingDocumentButtonText}
                fallback={'Download the funding document'}
              />
            </Heading>
            <Paragraph colorVariant={'lighter'}>
              <Message
                text={outletCms?.downloadFundingDocumentDescription}
                fallback={'Lorem ipsum dolor mit'}
              />
            </Paragraph>
            <Button
              borderRadius={'md'}
              onClick={handleFundingDocumentDownload}
              isLoading={downloading}
              isDisabled={downloading || confirming}
            >
              <Message
                text={outletCms?.downloadFundingDocumentTitle}
                fallback={'Download document'}
              />
            </Button>
          </Flex>
          <Flex direction={'column'} gap={2}>
            <Heading variant={'h4'}>
              <Message
                text={outletCms?.uploadSignedDocumentTitle}
                fallback={'Upload your signed document'}
              />
            </Heading>
            <Paragraph colorVariant={'lighter'}>
              <Message
                text={outletCms?.uploadSignedDocumentDescription}
                fallback={'Lorem ipsum dolor mit'}
              />
            </Paragraph>
            <DocumentInput
              name={'uploadedFile'}
              onDrop={(acceptedFiles): void => {
                if (acceptedFiles.length > 0) {
                  setUploadedFile(acceptedFiles[0]);
                }
              }}
              accept={{ 'application/pdf': ['.pdf'] }}
            />
          </Flex>
        </BasicOutlet.Main>
      </BasicOutlet.Scrollable>
      <BasicOutlet.Footer>
        <Button
          flexGrow={1}
          variant="secondary"
          onClick={goBack}
          isDisabled={downloading || confirming}
        >
          <Message text={commonCms?.cancel} fallback={'Cancel'} />
        </Button>
        <Button
          flexGrow={3}
          isDisabled={uploadedFile === null}
          isLoading={confirming}
          onClick={handleSignedDocumentUpload}
        >
          <Message
            text={outletCms?.confirmSignatureButtonText}
            fallback={'Confirm signature'}
          />
        </Button>
      </BasicOutlet.Footer>
    </BasicOutlet>
  );
}
